import 'video.js/dist/video-js.css';

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import videojs from 'video.js';
import type Player from 'video.js/dist/types/player';

import styles from './VideoPlayer.module.css';

interface VideoJsPlayerProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  onLoadingStateChange?: (isLoading: boolean) => void;
  autoplay?: boolean;
  poster?: string;
}

export const VideoJsPlayer = forwardRef(
  (
    props: VideoJsPlayerProps,
    ref // React.Ref<HTMLVideoElement>
  ) => {
    const { src, onLoadingStateChange, autoplay, poster, ...otherProps } = props;
    const videoNodeRef = useRef<HTMLVideoElement | null>(null);
    const playerRef = useRef<Player | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const lastReportedLoadingState = useRef<boolean | null>(null);
    const hasLoadedFrameRef = useRef(false);

    // Helper to safely update loading state (only if changed)
    const updateLoadingState = (isLoading: boolean) => {
      if (onLoadingStateChange && lastReportedLoadingState.current !== isLoading) {
        lastReportedLoadingState.current = isLoading;
        onLoadingStateChange(isLoading);
      }
    };

    // Set video time to show first frame - equivalent to old VideoPlayer's setVideoTime()
    // This helps show preview even with HTTP 206 partial content
    const setVideoTime = (player: Player) => {
      if (!hasLoadedFrameRef.current && player.readyState() >= 2) {
        // HAVE_CURRENT_DATA or higher
        try {
          player.currentTime(0.001);
          hasLoadedFrameRef.current = true;
        } catch (error) {
          // Ignore errors when setting currentTime (may fail in some browsers)
          console.warn('Failed to set video time:', error);
        }
      }
    };

    // Initialize Video.js when ref is set (ref callback pattern)
    // This guarantees element is in DOM when initialization happens
    const initializeVideoJs = (videoElement: HTMLVideoElement) => {
      // Video.js configuration (optimized for Cloudinary HTTP 206)
      const videoJsOptions = {
        autoplay: autoplay ? 'any' : false,
        muted: autoplay || false, // CRITICAL: Mute when autoplaying to comply with browser policies
        controls: false, // Use custom controls
        responsive: true,
        fluid: true,
        preload: 'metadata' as const, // Load only metadata + first frame for preview (uses HTTP 206)
        playsinline: true,
        poster: poster || undefined,
        html5: {
          vhs: {
            overrideNative: true // Better HLS/DASH support for Cloudinary
          },
          nativeVideoTracks: false,
          nativeAudioTracks: false,
          nativeTextTracks: false
        },
        sources: [
          {
            src: src || '',
            type: 'video/mp4'
          }
        ]
      };

      if (autoplay) {
        console.log('[VideoJsPlayer] Initializing with autoplay, muted:', videoJsOptions.muted);
      }

      // Initialize Video.js player
      let player: Player | null = null;
      try {
        player = videojs(videoElement, videoJsOptions);
        playerRef.current = player;
      } catch (error) {
        // If initialization fails, log but don't crash
        console.warn('Video.js initialization failed:', error);
        return;
      }

      if (!player) {
        return;
      }

      // Use Video.js ready() callback (Cloudinary recommended pattern)
      // This ensures player is fully initialized before setting up event listeners
      player.ready(() => {
        // Check if component was unmounted during async ready callback
        const currentPlayer = playerRef.current;
        if (!currentPlayer) {
          return;
        }

        // Auto-play if requested - use event-driven approach
        if (autoplay) {
          let hasAttemptedAutoPlay = false;

          const attemptAutoPlay = () => {
            if (hasAttemptedAutoPlay) return;

            const readyState = currentPlayer.readyState();
            console.log('[VideoJsPlayer] Attempting auto-play, readyState:', readyState);

            if (readyState >= 2) {
              hasAttemptedAutoPlay = true;
              currentPlayer
                .play()
                ?.then(() => {
                  console.log('[VideoJsPlayer] Auto-play succeeded');
                  setIsPlaying(true);
                })
                .catch((error) => {
                  console.log('[VideoJsPlayer] Auto-play failed:', error.name);
                  if (error.name === 'NotAllowedError') {
                    console.log('Auto-play was prevented by browser policy');
                  } else if (error.name === 'AbortError') {
                    console.log('Video play operation was aborted');
                  } else {
                    console.warn('Auto-play failed:', error);
                  }
                  hasAttemptedAutoPlay = false; // Allow retry
                });
            }
          };

          // Listen to events that indicate video is ready
          currentPlayer.one('loadedmetadata', () => {
            console.log('[VideoJsPlayer] loadedmetadata event fired');
            attemptAutoPlay();
          });

          currentPlayer.one('loadeddata', () => {
            console.log('[VideoJsPlayer] loadeddata event fired');
            attemptAutoPlay();
          });

          currentPlayer.one('canplay', () => {
            console.log('[VideoJsPlayer] canplay event fired');
            attemptAutoPlay();
          });

          // Try immediately if already loaded
          if (currentPlayer.readyState() >= 2) {
            console.log('[VideoJsPlayer] Video already loaded, attempting auto-play immediately');
            attemptAutoPlay();
          }
        }

        // Map Video.js events to loading state
        const handleLoadStart = () => {
          updateLoadingState(true);
        };

        const handleLoadedMetadata = () => {
          // Metadata loaded - try to show first frame
          setVideoTime(currentPlayer);
          // Don't set loading to false yet, wait for canplaythrough
        };

        const handleLoadedData = () => {
          // First frame loaded - ensure it's displayed
          setVideoTime(currentPlayer);
          // Check if can play through
          if (currentPlayer.readyState() >= 3) {
            updateLoadingState(false);
          }
        };

        const handleCanPlay = () => {
          // Can start playing, but may still buffer
          // Use canplaythrough for better HTTP 206 handling
        };

        const handleCanPlayThrough = () => {
          // Can play through without stopping - best for HTTP 206
          updateLoadingState(false);
        };

        const handleWaiting = () => {
          // Waiting for more data (buffering during playback)
          updateLoadingState(true);
        };

        const handlePlaying = () => {
          // Actually playing - buffering complete
          updateLoadingState(false);
        };

        const handleError = () => {
          const error = currentPlayer.error();
          if (error) {
            // Log errors but don't spam console
            if (error.code === 4) {
              // MEDIA_ERR_SRC_NOT_SUPPORTED
              console.error('Video codec not supported:', {
                message: error.message,
                code: error.code,
                src
              });
            } else if (error.code === 1 || error.code === 2 || error.code === 3) {
              // Other media errors - suppress but log for debugging
              console.log('Video error suppressed:', error.message || 'Unknown video error');
            } else {
              console.warn('Video error:', {
                message: error.message,
                code: error.code,
                src
              });
            }
          }
          // Notify parent that loading has stopped (due to error)
          updateLoadingState(false);
        };

        const handlePlay = () => {
          setIsPlaying(true);
        };

        const handlePause = () => {
          setIsPlaying(false);
        };

        const handleEnded = () => {
          setIsPlaying(false);
        };

        // Add event listeners (after player is ready)
        currentPlayer.on('loadstart', handleLoadStart);
        currentPlayer.on('loadedmetadata', handleLoadedMetadata);
        currentPlayer.on('loadeddata', handleLoadedData);
        currentPlayer.on('canplay', handleCanPlay);
        currentPlayer.on('canplaythrough', handleCanPlayThrough);
        currentPlayer.on('waiting', handleWaiting);
        currentPlayer.on('playing', handlePlaying);
        currentPlayer.on('error', handleError);
        currentPlayer.on('play', handlePlay);
        currentPlayer.on('pause', handlePause);
        currentPlayer.on('ended', handleEnded);

        // Check initial state after ready
        if (currentPlayer.readyState() >= 3) {
          updateLoadingState(false);
          setVideoTime(currentPlayer);
        } else if (currentPlayer.readyState() === 0) {
          updateLoadingState(true);
        }
      });
    };

    // Ref callback - fires when element is mounted (guarantees it's in DOM)
    const videoRefCallback = (node: HTMLVideoElement | null) => {
      // Cleanup previous player if ref changes
      if (videoNodeRef.current && playerRef.current && node !== videoNodeRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
        hasLoadedFrameRef.current = false;
      }

      videoNodeRef.current = node;

      if (node) {
        // Element is mounted - initialize Video.js immediately
        // Use setTimeout to ensure DOM is fully ready
        setTimeout(() => {
          if (node && videoNodeRef.current === node && !playerRef.current) {
            initializeVideoJs(node);
          }
        }, 0);
      }
    };

    // Cleanup on unmount or src change
    useEffect(() => {
      return () => {
        if (playerRef.current) {
          playerRef.current.dispose();
          playerRef.current = null;
        }
        hasLoadedFrameRef.current = false;
        lastReportedLoadingState.current = null;
      };
    }, [src]);

    // Reset loading state when src changes
    useEffect(() => {
      hasLoadedFrameRef.current = false;
      lastReportedLoadingState.current = null;
    }, [src]);

    // Reset loading state when src changes
    useEffect(() => {
      lastReportedLoadingState.current = null;
    }, [src]);

    const togglePlay = async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (playerRef.current) {
        if (playerRef.current.paused()) {
          try {
            await playerRef.current.play();
            setIsPlaying(true);
          } catch (error) {
            // Handle AbortError and other play failures gracefully
            if (error instanceof Error && error.name === 'AbortError') {
              console.log('Video play operation was aborted');
            } else {
              console.warn('Video play failed:', error);
            }
            setIsPlaying(false);
          }
        } else {
          playerRef.current.pause();
          setIsPlaying(false);
        }
      }
    };

    // Expose play/pause methods via ref (for Carousel compatibility)
    useImperativeHandle(ref, () => ({
      play: async () => {
        if (playerRef.current) {
          try {
            await playerRef.current.play();
            setIsPlaying(true);
          } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') {
              console.log('Video play operation was aborted');
            } else {
              console.warn('Video play failed:', error);
            }
            setIsPlaying(false);
          }
        }
      },
      pause: () => {
        if (playerRef.current) {
          playerRef.current.pause();
          setIsPlaying(false);
        }
      }
    }));

    return (
      <div className={styles.videoContainer}>
        {/* eslint-disable jsx-a11y/media-has-caption */}
        <video
          ref={videoRefCallback}
          className={`video-js vjs-default-skin ${styles.videoPlayer}`}
          {...otherProps}
          playsInline
        />
        <button className={styles.playButton} onClick={togglePlay} type="button">
          {isPlaying ? (
            <svg
              viewBox="0 0 24.00 24.00"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              stroke="#ffffff"
              strokeWidth="1"
            >
              <g
                id="SVGRepo_bgCarrier"
                strokeWidth="0"
                transform="translate(4.4399999999999995,4.4399999999999995), scale(0.63)"
              >
                <rect
                  x="0"
                  y="0"
                  width="24.00"
                  height="24.00"
                  rx="12"
                  fill="#ffffff"
                  strokeWidth="0"
                />
              </g>
              <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />
              <g id="SVGRepo_iconCarrier">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM10 9C10.5523 9 11 9.44772 11 10V14C11 14.5523 10.5523 15 10 15C9.44772 15 9 14.5523 9 14V10C9 9.44772 9.44772 9 10 9ZM14 9C14.5523 9 15 9.44772 15 10V14C15 14.5523 14.5523 15 14 15C13.4477 15 13 14.5523 13 14V10C13 9.44772 13.4477 9 14 9Z"
                  fill="#000000"
                />
              </g>
            </svg>
          ) : (
            <svg
              viewBox="0 0 24.00 24.00"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              stroke="#ffffff"
              strokeWidth="1"
            >
              <g
                id="SVGRepo_bgCarrier"
                strokeWidth="0"
                transform="translate(4.800000000000001,4.800000000001), scale(0.6)"
              >
                <rect
                  x="0"
                  y="0"
                  width="24.00"
                  height="24.00"
                  rx="12"
                  fill="#ffffff"
                  strokeWidth="0"
                />
              </g>
              <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round" />
              <g id="SVGRepo_iconCarrier">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM15.75 13.299C16.75 12.7217 16.75 11.2783 15.75 10.7009L11.25 8.10286C10.25 7.52551 9 8.24719 9 9.4019V14.598C9 15.7527 10.25 16.4744 11.25 15.8971L15.75 13.299Z"
                  fill="#000000"
                />
              </g>
            </svg>
          )}
        </button>
      </div>
    );
  }
);
VideoJsPlayer.displayName = 'VideoJsPlayer';
